import { gql, useQuery } from '@apollo/client';
import * as React from 'react';

import type {
  GetFinancialAccountBalanceAtQuery,
  GetFinancialAccountBalanceAtQueryVariables,
} from './__generated__/useFinancialAccountBalanceAt.gql';
import type { IBalanceSnapshot } from '../../utils/account/balanceHistory';

const GET_FINANCIAL_ACCOUNT_BALANCE_AT = gql`
  query GetFinancialAccountBalanceAt($id: ID!, $date: DateTimeISO!) {
    getFinancialAccount(id: $id) {
      id
      balanceAt(date: $date) {
        id
        balance
        timestamp
      }
    }
  }
`;

/**
 * One account's recorded value at or before `date`.
 *
 * The server answers for investment accounts only. Null while skipped, loading without a cached
 * result, on error, and when the account has no snapshot that old, so a caller reads "no past
 * value" rather than zero.
 *
 * @param date `null` skips the read. Keep it stable across renders: it is the query variable and
 *   the cache key.
 */
export const useFinancialAccountBalanceAt = (accountId: string, date: Date | null): IBalanceSnapshot | null => {
  // A snapshot at or before a fixed past instant does not change, so the cache is authoritative.
  const { data } = useQuery<GetFinancialAccountBalanceAtQuery, GetFinancialAccountBalanceAtQueryVariables>(
    GET_FINANCIAL_ACCOUNT_BALANCE_AT,
    {
      variables: date ? { id: accountId, date: date.toISOString() } : undefined,
      skip: date === null,
      fetchPolicy: 'cache-first',
    }
  );

  const balanceAt = data?.getFinancialAccount?.balanceAt ?? null;

  return React.useMemo(
    () => (balanceAt ? { balance: parseFloat(balanceAt.balance), timestamp: balanceAt.timestamp } : null),
    [balanceAt]
  );
};
